import * as React from 'react';

import {
    ApplicationHelpType,
    ReportSettings_PlaintiffsFirmDisplayMode,
    ReportSettings_ReportCaseDetailsLastColumn,
    ReportSettings_ReportCaseDetailsLastColumnDisplayName,
    TimelineReportSections,
} from '../../../utilities/Constants';
import { CloseButton, Modal, Stack } from 'react-bootstrap';
import { CollabModalTypes, useCollabReportModal } from '../collab-report/modals/CollabModalContext';
import {
    IReportSectionDetailModel,
    ITimelineReportInputModel,
} from '../../../interfaces/Report/ITimelineReport';

import CheckmateSelect from '../../../components/shared/CheckmateSelect';
import CheckmateSelectHelper from '../../../utilities/CheckmateSelectHelper';
import Common from '../../../stores/Common';
import { Help } from '../../../components/shared/Help';
import { ILookupModel } from '../../../interfaces/ILookup';
import { IValidation } from '../../../interfaces/IError';
import { cloneDeep } from 'lodash';

export interface IReportSettingsModalProps {
    defaultReportInputModel: ITimelineReportInputModel;
    disabled?: boolean;
    getSectionsAfterSectionCheckedChange: (
        event: React.ChangeEvent<HTMLInputElement>,
        section: TimelineReportSections,
        sections: IReportSectionDetailModel[]
    ) => IReportSectionDetailModel[];
    initialInputModel: ITimelineReportInputModel;
    onHide: () => void;
    onSubmit: (inputModel: ITimelineReportInputModel) => void;
    open: boolean;
    readonly: boolean;
    reportGuid?: string;
    sections: ILookupModel[];
    setValidation: React.Dispatch<React.SetStateAction<IValidation>>;
    validation?: IValidation;
}

export function ReportSettingsModal({
    defaultReportInputModel,
    disabled,
    getSectionsAfterSectionCheckedChange,
    initialInputModel,
    onHide,
    onSubmit,
    open,
    readonly,
    reportGuid,
    sections,
    setValidation,
    validation,
}: IReportSettingsModalProps) {
    if (!open) return null;

    const modal = useCollabReportModal();

    const [localInputModel, setLocalInputModel] =
        React.useState<ITimelineReportInputModel>(initialInputModel);

    const validate = (model: ITimelineReportInputModel): boolean => {
        const validation: IValidation = {};

        if (!model.startDate || !model.endDate) {
            validation.settings = ['Enter Report Parameters'];
            setValidation(validation);
            return false;
        }

        if (Common.dateDiffInDays(model.endDate, model.startDate) < 0) {
            validation.settings = ['From Date must be before To Date'];
            setValidation(validation);
            return false;
        }

        if ((model.reportSectionDetails?.length ?? 0) === 0) {
            validation.settings = ['Select at least 1 section'];
            setValidation(validation);
            return false;
        }

        if (
            model.reportSectionDetails?.some((element) =>
                [
                    TimelineReportSections.RiskIssues,
                    TimelineReportSections.SignificantActivity,
                    TimelineReportSections.UpcomingTrialDates,
                    TimelineReportSections.TenderedCases,
                ].includes(element.section)
            ) &&
            (!model.additionalSettings ||
                !model.additionalSettings.reportCaseDetailsLastColumnDisplayName)
        ) {
            validation.settings = ['Case Details - Last Column Header Name is required'];
            setValidation(validation);
            return false;
        }

        setValidation(validation);
        return true;
    };

    const handleClearSettings = () => {
        setValidation({});
        setLocalInputModel(defaultReportInputModel);
    };

    const handleClearClick = () => {
        modal.openModal(CollabModalTypes.ClearSettingConfirm, null, () => {
            handleClearSettings();
            modal.closeModal(CollabModalTypes.ClearSettingConfirm);
        });
    };

    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const localValidation = { ...validation };
        const localReportStateCopy = cloneDeep(localInputModel);

        const { name, value } = event.target;

        switch (name) {
            case 'startDate':
                localReportStateCopy.startDate = value;
                break;
            case 'endDate':
                localReportStateCopy.endDate = value;
                break;
            case ReportSettings_ReportCaseDetailsLastColumnDisplayName:
                if (!localReportStateCopy.additionalSettings) {
                    localReportStateCopy.additionalSettings = {};
                }
                localReportStateCopy.additionalSettings.reportCaseDetailsLastColumnDisplayName =
                    value;
                if (value) localValidation.settings = [];
                break;
            default:
                break;
        }

        setValidation((prevState) => ({ ...prevState, ...localValidation }));
        setLocalInputModel(localReportStateCopy);
    };

    const handleCheckmateSelectChange = (selectedItem: any, key: string) => {
        if (selectedItem === null || !selectedItem.id) return;

        const localReportStateCopy = cloneDeep(localInputModel);

        if (!localReportStateCopy.additionalSettings) {
            localReportStateCopy.additionalSettings = {};
        }

        switch (key) {
            case ReportSettings_PlaintiffsFirmDisplayMode:
                localReportStateCopy.additionalSettings.plaintiffsFirmDisplayMode = selectedItem.id;
                break;
            case ReportSettings_ReportCaseDetailsLastColumn:
                localReportStateCopy.additionalSettings.reportCaseDetailsLastColumn =
                    selectedItem.id;
                localReportStateCopy.additionalSettings.reportCaseDetailsLastColumnDisplayName = '';

                break;
            default:
                break;
        }

        setLocalInputModel(localReportStateCopy);
    };

    const handleSectionCheckedChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        section: TimelineReportSections
    ) => {
        const isChecked = event.target.checked;
        const found = localInputModel.reportSectionDetails?.find((x) => x.section === section);

        if (!isChecked && found?.comment) {
            const onConfirm = () => {
                const found = localInputModel.reportSectionDetails?.find(
                    (x) => x.section === section
                );

                if (found) {
                    found.excludeInReport = true;

                    const newSectionDetails = getSectionsAfterSectionCheckedChange(
                        event,
                        section,
                        localInputModel.reportSectionDetails ?? []
                    );
                    setLocalInputModel((prev) => ({
                        ...prev,
                        reportSectionDetails: newSectionDetails,
                    }));
                }

                modal.closeModal(CollabModalTypes.RemoveSection);
            };

            modal.openModal(CollabModalTypes.RemoveSection, undefined, onConfirm);
        } else {
            const newSectionDetails = getSectionsAfterSectionCheckedChange(
                event,
                section,
                localInputModel.reportSectionDetails ?? []
            );
            setLocalInputModel((prev) => ({
                ...prev,
                reportSectionDetails: newSectionDetails,
            }));
        }
    };

    const handleRunReportClick = () => {
        const isValid = validate(localInputModel);
        if (isValid) {
            onSubmit(localInputModel);
        }
    };

    const plaintiffOptions = CheckmateSelectHelper.getPlaintiffsFirmDisplayOptions();
    const availableColumns = CheckmateSelectHelper.getAdditionalCaseIdentifierOptions();

    return (
        <Modal
            centered
            size="lg"
            show={open}
            onHide={onHide}
            backdrop={false}
            dialogClassName="board-settings-dialog"
        >
            <Modal.Header>
                <Stack
                    direction="horizontal"
                    style={{ justifyContent: 'space-between', width: '100%' }}
                >
                    <Modal.Title>Report Settings</Modal.Title>
                    <CloseButton onClick={onHide} />
                </Stack>
            </Modal.Header>
            <Modal.Body>
                <div className="row">
                    <span className="text-danger">{validation?.settings}</span>
                </div>
                <div className="row mt-2">
                    <div className="col-sm-2">
                        <label className="control-label">Date Range</label>
                    </div>
                    <div className="col-sm-10">
                        <input
                            type="date"
                            name="startDate"
                            disabled={disabled || !!reportGuid}
                            className={
                                'form-control d-inline-block horizontal-margin' +
                                (Common.isValidDate(localInputModel.startDate ?? '')
                                    ? ''
                                    : ' unselectClass')
                            }
                            value={localInputModel.startDate ?? ''}
                            onChange={handleChange}
                        />
                        <span className="horizontal-margin">to</span>
                        <input
                            type="date"
                            name="endDate"
                            disabled={disabled || !!reportGuid}
                            className={
                                'form-control d-inline-block horizontal-margin' +
                                (Common.isValidDate(localInputModel.endDate ?? '')
                                    ? ''
                                    : ' unselectClass')
                            }
                            value={localInputModel.endDate ?? ''}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="row my-2">
                    <label className="control-label">Sections</label>
                </div>
                {sections.map((section: ILookupModel) => (
                    <SectionSettings
                        key={section.id}
                        section={section}
                        checked={
                            !!localInputModel.reportSectionDetails?.some(
                                (x) => x.section === section.id
                            )
                        }
                        onChange={handleSectionCheckedChange}
                        disabled={disabled}
                    />
                ))}

                {localInputModel.reportSectionDetails?.some(
                    (element) =>
                        element.section === TimelineReportSections.RiskIssues ||
                        element.section === TimelineReportSections.SignificantActivity ||
                        element.section === TimelineReportSections.UpcomingTrialDates ||
                        element.section === TimelineReportSections.TenderedCases
                ) && (
                    <>
                        <div className="row my-2">
                            <label className="control-label">
                                Case Details
                                <Help
                                    type={ApplicationHelpType.Info}
                                    title="Case Details"
                                    helpText="The settings under the Case Details applies to the following sections: Tendered Cases, Risk Issues, Significant Activity and Upcoming Trial Dates."
                                />
                            </label>
                        </div>
                        <>
                            <div className="row mt-1 ms-3">
                                <div className="col-sm-4">
                                    <label className="col-form-label text-gray">
                                        Plaintiffs Firm *
                                    </label>
                                </div>
                                <div className="col-sm-8">
                                    <CheckmateSelect
                                        options={plaintiffOptions}
                                        name={ReportSettings_PlaintiffsFirmDisplayMode}
                                        value={plaintiffOptions.find(
                                            (opt) =>
                                                opt.value ===
                                                localInputModel.additionalSettings?.plaintiffsFirmDisplayMode?.toString()
                                        )}
                                        onChange={(e: any) => {
                                            handleCheckmateSelectChange(
                                                e,
                                                ReportSettings_PlaintiffsFirmDisplayMode
                                            );
                                        }}
                                        isDisabled={disabled}
                                    />
                                </div>
                            </div>
                            <div className="row mt-1 ms-3">
                                <div className="col-sm-4">
                                    <label className="col-form-label text-gray">
                                        Last Column *
                                    </label>
                                </div>
                                <div className="col-sm-8">
                                    <CheckmateSelect
                                        options={availableColumns}
                                        name={ReportSettings_ReportCaseDetailsLastColumn}
                                        value={availableColumns.find(
                                            (opt) =>
                                                opt.value ===
                                                localInputModel.additionalSettings?.reportCaseDetailsLastColumn?.toString()
                                        )}
                                        onChange={(e: any) => {
                                            handleCheckmateSelectChange(
                                                e,
                                                ReportSettings_ReportCaseDetailsLastColumn
                                            );
                                        }}
                                        isDisabled={disabled}
                                    />
                                </div>
                            </div>
                            <div className="row mt-1 ms-3">
                                <div className="col-sm-4">
                                    <label className="col-form-label text-gray">
                                        Last Column Header Name *
                                    </label>
                                </div>
                                <div className="col-sm-8">
                                    <input
                                        className="form-control"
                                        type="text"
                                        name={ReportSettings_ReportCaseDetailsLastColumnDisplayName}
                                        placeholder="Header Name"
                                        value={
                                            localInputModel.additionalSettings
                                                ?.reportCaseDetailsLastColumnDisplayName ?? ''
                                        }
                                        onChange={handleChange}
                                        disabled={disabled}
                                    />
                                </div>
                            </div>
                        </>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {reportGuid ? (
                    <>
                        {disabled || readonly ? (
                            <button className="btn btn-default" onClick={onHide}>
                                Close
                            </button>
                        ) : (
                            <>
                                <button
                                    className="btn btn-orange text-gray"
                                    onClick={handleRunReportClick}
                                >
                                    Run Report
                                </button>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <button className="btn btn-default" onClick={handleClearClick}>
                            Clear
                        </button>
                        <button className="btn btn-orange text-gray" onClick={handleRunReportClick}>
                            Run Report
                        </button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function SectionSettings({
    disabled,
    section,
    checked,
    onChange,
}: {
    disabled?: boolean;
    section: ILookupModel;
    checked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>, section: TimelineReportSections) => void;
}) {
    return (
        <div className="mt-2">
            <label>
                <input
                    id={'chkSection_' + section.id}
                    className="form-check-input d-inline-block mt-1"
                    type="checkbox"
                    checked={checked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onChange(e, section.id);
                    }}
                    disabled={disabled}
                />
                <span className="ms-2 align-top">{section.displayName}</span>
            </label>
        </div>
    );
}
